import { useEffect, useState } from 'react'
import { Container, Row, Col, Card, Nav, Badge, Alert, Pagination } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getSessions } from '../../../actions/classActions'
import Loader from '../../../components/loaders/Loader'

const ITEMS_PER_PAGE = 9

export default function ClassList() {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { sessions, loading, dashboardError } = useSelector(state => state.classes)
    const [filter, setFilter] = useState('all')
    const [page, setPage] = useState(1)

    useEffect(() => {
        dispatch(getSessions(filter))
    }, [dispatch, filter])

    useEffect(() => {
        setPage(1)
    }, [filter])

    if (loading && (!sessions || sessions.length === 0)) return <Loader />

    if (dashboardError) return (
        <Container className='my-3 p-5'>
            <Alert variant='danger'>{dashboardError}</Alert>
        </Container>
    )

    const totalPages = Math.ceil(sessions.length / ITEMS_PER_PAGE)
    const paginatedSessions = sessions.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE)

    const stateLabel = (state) => {
        switch (state) {
            case 'planned': return { text: t('teacher.classes.pending'), variant: 'primary' }
            case 'in_progress': return { text: t('teacher.classes.inprogress'), variant: 'warning' }
            case 'done': return { text: t('teacher.classes.completed'), variant: 'success' }
            case 'cancelled': return { text: t('teacher.classes.cancelled'), variant: 'secondary' }
            default: return { text: state, variant: 'light' }
        }
    }

    return (
        <Container className='my-3 p-5'>
            <Row>
                <Col>
                    <h3 className='fw-bold mb-1'>{t('teacher.classes.title')}</h3>
                    <p className='text-muted mb-4'>{sessions.length} {t('teacher.classes.subtitle')}</p>
                </Col>
            </Row>

            <Row className='mb-3'>
                <Col>
                    <Nav variant="pills" activeKey={filter} onSelect={(k) => setFilter(k)}>
                        <Nav.Item><Nav.Link eventKey="all">{t('teacher.classes.all')}</Nav.Link></Nav.Item>
                        <Nav.Item><Nav.Link eventKey="today">{t('teacher.classes.today')}</Nav.Link></Nav.Item>
                        <Nav.Item><Nav.Link eventKey="week">{t('teacher.classes.thisweek')}</Nav.Link></Nav.Item>
                    </Nav>
                </Col>
            </Row>

            <Row className='g-3'>
                {paginatedSessions.length > 0
                    ? paginatedSessions.map(session => {
                        const st = stateLabel(session.state)
                        return (
                            <Col key={session.id} xs={12} md={6} xl={4}>
                                <Card className='border-0 shadow-sm rounded-4 h-100'>
                                    <Card.Body className='p-4'>
                                        <div className='d-flex justify-content-between align-items-start mb-2'>
                                            <div>
                                                <h5 className='fw-bold mb-1'>{session.groupName}</h5>
                                                <p className='text-muted mb-0' style={{ fontSize: '13px' }}>{session.courseName}</p>
                                            </div>
                                            <div className='d-flex gap-2'>
                                                {session.isSubstitute && (
                                                    <Badge bg='info' className='rounded-pill'>{t('teacher.classes.substitute')}</Badge>
                                                )}
                                                <Badge bg={st.variant} className='rounded-pill'>{st.text}</Badge>
                                            </div>
                                        </div>
                                        <div className='mb-3'>
                                            <p className='mb-1' style={{ fontSize: '14px', color: '#374151' }}>
                                                <strong>{session.date}</strong>
                                            </p>
                                            <p className='mb-0' style={{ fontSize: '14px', color: '#6b7280' }}>
                                                {session.startTime} - {session.endTime}
                                            </p>
                                        </div>
                                        {(session.classroom || session.center) && (
                                            <p className='mb-2' style={{ fontSize: '13px', color: '#6b7280' }}>
                                                {session.classroom && <><strong>{t('teacher.classes.classroom')}</strong> {session.classroom}</>}
                                                {session.classroom && session.center && ' · '}
                                                {session.center && <>{session.center}</>}
                                            </p>
                                        )}
                                        {session.isOnline && (
                                            <p className='mb-2' style={{ fontSize: '13px', color: '#dc2626' }}>{t('teacher.classes.online')}</p>
                                        )}
                                        <Link
                                            to={`/teacher/classes/session/${session.id}`}
                                            className='btn btn-primary w-100 rounded-3'
                                        >
                                            {session.state === 'planned' || session.state === 'in_progress'
                                                ? t('teacher.classes.session')
                                                : t('teacher.classes.verdetails')
                                            }
                                        </Link>
                                    </Card.Body>
                                </Card>
                            </Col>
                        )
                    })
                    : <Col><p className='text-muted'>{t('teacher.classes.nosessions')}</p></Col>
                }
            </Row>

            {totalPages > 1 && (
                <Row className='mt-4'>
                    <Col className='d-flex justify-content-center'>
                        <Pagination>
                            <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
                            <Pagination.Prev onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} />
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                                .reduce((acc, p, idx, arr) => {
                                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...')
                                    acc.push(p)
                                    return acc
                                }, [])
                                .map((p, idx) =>
                                    p === '...'
                                        ? <Pagination.Ellipsis key={`ellipsis-${idx}`} disabled />
                                        : <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>
                                )
                            }
                            <Pagination.Next onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
                            <Pagination.Last onClick={() => setPage(totalPages)} disabled={page === totalPages} />
                        </Pagination>
                    </Col>
                </Row>
            )}
        </Container>
    )
}
